import { saveAs } from 'file-saver';

import { Config } from '../config';
import { Device, OTA, OTAFileCategory, OTAFileV2, OTAGroup, OTAGroupStats } from '../interfaces';
import { deepCopy } from '../utils';
import { DevicesService } from './devices.service';
import { HttpService } from './http.service';

interface OTAGroupUpdateRequestBody {
  ota_id: number | null;
  name: string;
  device_hw_version_id?: number;
}

export abstract class OTAService {
  public static getOTAConfiguration = (installedOTAid: number, muteErrors = false): Promise<OTA> => {
    return HttpService.fetchAuthorized(
      `${Config.APIv2}/ota/${installedOTAid}`,
      {
        cache: 'no-store',
      },
      undefined,
      muteErrors,
    ).then((res) => res);
  };

  public static getGroups = (pageSize = 999, page = 0): Promise<OTAGroup[]> => {
    const query = new URLSearchParams({ size: pageSize.toString(), page: page.toString() });
    return HttpService.fetchAuthorized(`${Config.APIv2}/ota_groups?${query}`).then((res: OTAGroup[]) => {
      if (!res) {
        return [];
      }

      // in res, for each object in ota.content.files sort files by key field using localeCompare
      res.forEach((entry) => entry.ota?.content?.files?.sort((a, b) => a.key.localeCompare(b.key)));
      return res;
    });
  };

  public static getGroupStats = (): Promise<OTAGroupStats[]> => {
    return HttpService.fetchAuthorized(`${Config.APIv2}/ota_groups/stats`).then((res: OTAGroupStats[]) => res);
  };

  public static getGroupsByFleetId = (selectedFleetId, pageSize = 999, page = 0): Promise<OTAGroup[]> => {
    const query = new URLSearchParams({
      size: pageSize.toString(),
      page: page.toString(),
      fleet_id: selectedFleetId.toString(),
    });
    return HttpService.fetchAuthorizedFleetless(
      `${Config.APIv2}/ota_groups?${query}`,
      {
        method: 'GET',
      },
      null,
      true,
    ).then((res: OTAGroup[]) => {
      if (!res) {
        return [];
      }

      // in res, for each object in ota.content.files sort files by key field using localeCompare
      res.forEach((entry) => entry.ota?.content?.files?.sort((a, b) => a.key.localeCompare(b.key)));
      return res;
    });
  };

  public static getOTAGroup = (otaGroupId: number): Promise<OTAGroup> => {
    return HttpService.fetchAuthorized(`${Config.APIv2}/ota_groups/${otaGroupId}`).then((res: OTAGroup) => {
      if (!res) {
        return null;
      }

      // in res, for each object in ota.content.files sort files by key field using localeCompare
      res.ota?.content?.files?.sort((a, b) => a.key.localeCompare(b.key));
      return res;
    });
  };

  public static createGroup = (body: Partial<OTAGroup>): Promise<OTAGroup> => {
    return HttpService.fetchAuthorized(`${Config.APIv2}/ota_groups`, {
      method: 'POST',
      body: JSON.stringify(body),
    }).then((res) => res);
  };

  public static updateGroup = (group: OTAGroup, payload: OTAGroupUpdateRequestBody): Promise<OTAGroup> => {
    return HttpService.fetchAuthorized(
      `${Config.APIv2}/ota_groups/${group.id}`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
      null,
      true,
    ).then((res) => res);
  };

  public static getOTAConfigurations = (): Promise<OTA[]> => {
    return HttpService.fetchAuthorized(
      `${Config.APIv2}/ota`,
      {
        method: 'GET',
      },
      null,
      true,
    ).then((res) => res);
  };

  // FIXME typings
  public static getHistory = (groupId: number): Promise<OTA[]> => {
    return HttpService.fetchAuthorized(
      `${Config.APIv2}/ota_groups/${groupId}/history`,
      {
        method: 'GET',
      },
      null,
      true,
    ).then((res) => res);
  };

  public static createOTA = (body: Partial<OTA>): Promise<OTA> => {
    return HttpService.fetchAuthorized(
      `${Config.APIv2}/ota`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
      null,
      true,
    ).then((res) => res);
  };

  public static createOTAFleetless = (id, body: Partial<OTA>): Promise<OTA> => {
    const query = new URLSearchParams({ fleet_id: id.toString() });

    return HttpService.fetchAuthorizedFleetless(
      `${Config.APIv2}/ota?${query}`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
      null,
      true,
    ).then((res) => res);
  };

  public static updateOTA = (id: number, body: Partial<OTA>): Promise<OTA> => {
    return HttpService.fetchAuthorized(
      `${Config.APIv2}/ota/${id}`,
      {
        method: 'PUT',
        body: JSON.stringify(body),
      },
      null,
      true,
    ).then((res) => res);
  };

  public static assignDevicesToOtaGroup = (groupId: string, body: { add?: string[]; remove?: string[] }) => {
    return HttpService.fetchAuthorized(`${Config.APIv2}/ota_groups/${groupId}/devices`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }).then((res) => res);
  };

  public static unassignDevicesFromOtaGroup = (devices: Device[]): Promise<Device[]> => {
    // group devices by ota_group_id
    const groups: { [key: string]: Device[] } = {};
    devices.forEach((device) => {
      // skip devices that are already unassigned or have invalid ota_group_id
      if (device.ota_group_id === null || device.ota_group_id === undefined) {
        return;
      }

      const otaGroupId = String(device.ota_group_id);

      if (groups[otaGroupId]) {
        groups[otaGroupId].push(device);
      } else {
        groups[otaGroupId] = [device];
      }
    });

    // for each of these groups, run an unassign request
    const requests: any[] = [];
    Object.keys(groups).forEach((otaGroupId) => {
      requests.push(
        OTAService.assignDevicesToOtaGroup(otaGroupId, { remove: groups[otaGroupId].map((d) => `${d.id}`) }),
      );
    });

    return Promise.all(requests).then(() => devices);
  };

  public static assignSelectedDevicesToOtaGroup = (groupId: string | number, devices: Device[]): Promise<Device[]> => {
    const newOtaGroups: any = {};
    const data = devices.map((newDevice) => {
      const device = deepCopy(newDevice);
      const otaGroupId = groupId || device.ota_group_id;
      if (newOtaGroups[otaGroupId]) {
        newOtaGroups[otaGroupId].push(`${device.id}`);
      } else {
        newOtaGroups[otaGroupId] = [`${device.id}`];
      }
      device.ota_group_id = groupId;
      return device;
    });
    const reqKey = groupId ? 'add' : 'remove';
    const requests: any[] = [];
    Object.keys(newOtaGroups).forEach((otaGroupId) => {
      requests.push(OTAService.assignDevicesToOtaGroup(otaGroupId, { [reqKey]: newOtaGroups[otaGroupId] }));
    });
    return Promise.all(requests).then(() => data);
  };

  public static assignAllDevicesToOtaGroup = async (groupId: string): Promise<Device[]> => {
    const devices = await DevicesService.getAll();
    return OTAService.assignSelectedDevicesToOtaGroup(groupId, devices);
  };

  public static getOTAFiles = async (): Promise<OTAFileV2[]> => {
    const pageSize = 1000;
    const fetchPage = async (page: number): Promise<OTAFileV2[]> => {
      const res = await HttpService.fetchAuthorized(`${Config.APIv2}/ota_files?page=${page}&size=${pageSize}`);
      return res || [];
    };

    const allFiles: OTAFileV2[] = [];
    let pageResults: OTAFileV2[] = [];
    let page = 0;

    // eslint-disable-next-line no-constant-condition
    do {
      // eslint-disable-next-line no-await-in-loop
      pageResults = await fetchPage(page);
      allFiles.push(...pageResults);

      // If we received less than the page size, its the last page
      page++;
    } while (pageResults.length === pageSize);

    return allFiles;
  };

  public static getOTAFileCategories = (): Promise<OTAFileCategory[]> => {
    return HttpService.fetchAuthorized(`${Config.APIv2}/ota_files/categories`).then((res) => {
      if (!res) {
        return [];
      }
      return res.sort((a, b) => a.category.localeCompare(b.category));
    });
  };

  public static createOTAFileCategory = (category: OTAFileCategory): Promise<OTAFileCategory[]> => {
    return HttpService.fetchAuthorized(`${Config.APIv2}/ota_files/categories`, {
      method: 'POST',
      body: JSON.stringify(category),
    }).then((res) => res);
  };

  public static createOtaFile = (key: string, file: File, category = '', metadata = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('key', key);
    formData.append('category', category);
    Object.entries(metadata).forEach(([metadataKey, value]) => {
      formData.append(metadataKey, value);
    });

    return HttpService.fetchAuthorized(`${Config.APIv2}/ota_files/upload`, {
      method: 'POST',
      body: formData,
    }).then((res) => {
      return res;
    });
  };

  public static downloadOTAFileByContent = (file: OTAFileV2) => {
    const url = `${Config.APIv2}/ota_files/${file.id}/content`;
    return HttpService.downloadFile(url).then((res) => {
      if (res) {
        res.blob().then((blob) => {
          saveAs(blob, file.key);
        });
      }
    });
  };

  public static deleteOTAFile = (file: OTAFileV2) => {
    return HttpService.fetchAuthorized(
      `${Config.APIv2}/ota_files/${file.id}`,
      {
        method: 'DELETE',
      },
      undefined,
      true,
    ).then((res) => res);
  };

  public static deleteOtaGroup = (groupId: number) => {
    return HttpService.fetchAuthorized(`${Config.APIv2}/ota_groups/${groupId}`, {
      method: 'DELETE',
    }).then((res) => res);
  };

  public static deleteOtaConfig = (configId: number) => {
    return HttpService.fetchAuthorized(`${Config.APIv2}/ota/${configId}`, {
      method: 'DELETE',
    }).then((res) => res);
  };

  public static downloadOtaFile = (file: OTAFileV2) => {
    if (!file.presigned_url) {
      return OTAService.downloadOTAFileByContent(file);
    }

    return HttpService.downloadFile(file.presigned_url).then((res) => {
      if (res) {
        res.blob().then((blob) => {
          saveAs(blob, file.key);
        });
      }
    });
  };
}
